// PagesView.jsx — View PURA da /pages ("Páginas e links"). Recebe { vm, actions }.
// Contrato: pages.vm.js (@version 1). Sem fetch, sem formatação, sem regra de negócio: todo
// rótulo, data, URL montada e permissão (canPublish/canUnpublish/canDelete) vem pronto do vm.
// `useState` só de UI (o menu "mais ações" de uma linha) — nunca de dado.
//
// ═══════════════════════════════════════════════════════════════════════════════
// A DECISÃO DE DESENHO: TRÊS PESOS VISUAIS PARA QUATRO ESTADOS
//
// O defeito da tela antiga era tratar tudo igual. Aqui cada estado tem peso próprio:
//
//   pending / unpublished → PESO ALTO. O card ganha uma FAIXA de estado no topo (tint do
//        statusTone) com o statusLabel, o statusHint (o que o visitante vê) e o botão de
//        resolver. É a única forma da linha "parecer" o problema que ela é.
//   published             → PESO CALMO. Card branco, chip pequeno, nenhuma faixa, nenhum
//        botão preenchido. Quem está certo não pede nada.
//   draft                 → PESO BAIXO. Sai da lista de linhas e vira um LADRILHO menor,
//        em fundo --bg-page, sem endereço para copiar (rascunho não tem link que sirva).
//
// O ENDEREÇO É O PRODUTO: em quem está no ar, a URL fica num bloco próprio com um botão
// "Copiar link" ROTULADO (não um ícone no fim da linha) e o "abrir" ao lado. Em quem não
// está no ar, o endereço aparece apagado/riscado e SEM copiar — copiar link morto é armadilha.
//
// DOMÍNIO PRÓPRIO: quando `customDomain` existe, ele vira o endereço de cima, com selo e o
// statusLabel do próprio domínio. Com `isLive: false` (comprado, apontando, ainda não responde)
// o "abrir" fica travado e o endereço padrão é apresentado como o que ainda serve — dizer
// "No ar" ali seria mentira.
//
// A oferta de domínio é um elemento DESTA tela (nada compartilhado com a Home ou com a tela de
// Domínio próprio) e vem DEPOIS da lista: aqui ela é continuação, não interrupção. Ela e o CTA
// do estado vazio usam --color-cta (dinheiro/nossa intenção), não --color-primary.
//
// Consistência: mesmo CARD_SX (raio, borda, sombra, hover) de Serviços / Ausências / Domínio,
// mesmos chips de tint, mesmo StateBlock, mesmos IconButton de 40px. Neutros saem de
// --bg-page/--divider/--text-* (papéis themeáveis) e NUNCA da escala --slate-*, que é global
// fixa e clara (quebraria os temas de superfície escura).
//
// Responsividade: mesmo corte das telas irmãs — as linhas viram coluna única abaixo de `lg`.
import { useState } from 'react';
import {
  Box, Typography, Button, ButtonBase, IconButton, Tooltip, Menu, MenuItem, ListItemIcon,
  Skeleton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import PublishRounded from '@mui/icons-material/PublishRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import VerifiedRounded from '@mui/icons-material/VerifiedRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import LinkOffRounded from '@mui/icons-material/LinkOffRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import InsertLinkRounded from '@mui/icons-material/InsertLinkRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (mesmos papéis das outras telas)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
  brand: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)', sub: 'var(--color-primary-dark)', main: 'var(--color-primary)' },
};

const tint = (tone) => TINT[tone] || TINT.neutral;

// CTA COMERCIAL — só onde o interesse do clique é nosso (a oferta de domínio e o convite a
// criar a primeira página). Não herda --color-primary de propósito: a cor de marca é por nicho
// (vinho/vermelha em Salão e Tatuagem) e vermelho em botão de compra lê "pare".
// Fallback no verde ESCURO que já existe (--tint-success-fg): --color-success é cor de
// texto/ícone e como preenchimento reprova o contraste com --on-primary.
const CTA_FILL = 'var(--color-cta, var(--tint-success-fg))';
const CTA_FILL_DARK = 'var(--color-cta-dark, color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary)))';
const CTA_SOFT = `color-mix(in srgb, ${CTA_FILL} 12%, var(--surface))`;
const CTA_EDGE = `color-mix(in srgb, ${CTA_FILL} 34%, var(--surface))`;

// Peso de título na fonte de CORPO. A fonte do nicho saiu daqui: título de card, valor e número
// ficam na fonte de corpo (hierarquia por peso e tamanho) — só o título de página (h1) leva a fonte
// do nicho. Antes, em Barbearia, isto colocava display condensada em "R$ 8.430,00".
const HEAD = { fontWeight: 'var(--fw-bold)' };
// TÍTULO DE PÁGINA: <Typography variant="h1"> puro. Tamanho, peso, altura de linha e a redução
// no celular vivem no tema pinado (muiThemeConfig) — e a fonte do nicho entra aí, só aí.
// Endereço e título de 60 caracteres: sempre podem quebrar (mockContaCheia).
const BREAK = { overflowWrap: 'anywhere', lineHeight: 1.35 };

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: 0.8 },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const CTA_BTN_SX = {
  bgcolor: CTA_FILL, color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.5, minHeight: 46,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: CTA_FILL_DARK },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: 2 },
};

const OUTLINE_BTN_SX = {
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  border: '1px solid', borderColor: 'var(--divider)',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
};

const QUIET_BTN_SX = {
  minHeight: 42, px: 1.5, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
};

const DANGER_BTN_SX = {
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  bgcolor: 'var(--color-error)', color: 'var(--on-primary)', fontWeight: 'var(--fw-bold)',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--tint-error-fg)' },
  '&.Mui-disabled': { bgcolor: 'var(--tint-error-bg)', color: 'var(--tint-error-sub)' },
  '&:focus-visible': { outline: '2px solid var(--color-error)', outlineOffset: 2 },
};

const ICON_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
};

const DIALOG_PAPER_SX = {
  bgcolor: 'var(--surface)', color: 'var(--text-primary)',
  borderRadius: 'var(--radius-card)', border: '1px solid', borderColor: 'var(--divider)',
};

const DIALOG_TITLE_SX = {
  px: 3, pt: 3, pb: 1, ...HEAD, color: 'var(--text-primary)',
};

const MENU_PAPER_SX = {
  ...CARD_SX, borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-hover)', minWidth: 236,
};

/* Os quatro estados: ícone e peso. O TOM vem sempre do vm (`statusTone`). */
const STATUS_META = {
  published: { Icon: CheckCircleRounded, weight: 'calm' },
  pending: { Icon: WarningAmberRounded, weight: 'alert' },
  unpublished: { Icon: CloudOffRounded, weight: 'alert' },
  draft: { Icon: EditNoteRounded, weight: 'quiet' },
};

const TYPE_ICON = { 1: InsertLinkRounded, 2: PersonRounded, 3: StorefrontRounded };
const HEADLINE_ICON = {
  success: CheckCircleRounded, warning: WarningAmberRounded, error: ErrorRounded, neutral: LanguageRounded,
};
// Ícone por posição da razão no estado vazio (o texto é do vm).
const REASON_ICON = [CalendarMonthRounded, BoltRounded, TuneRounded];

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas
   ═══════════════════════════════════════════════════════════════════════════ */

/** Chip de estado — DOIS REGISTROS, um critério: o estado PEDE AÇÃO ou não.
 *   · error / warning  → pastilha preenchida (tint + fio + ícone): salta numa tabela de 40 linhas;
 *   · success / info / neutral → PONTO + rótulo, sem pastilha: a coluna fica calma e quem aparece
 *     é o dado. Numa lista longa, 40 pastilhas verdes competem com o conteúdo e escondem as duas
 *     vermelhas — que são as únicas que mudam o que o dono do negócio vai fazer agora.
 *  Estado nunca só por cor: o rótulo nomeia o estado nos dois registros (e a pastilha leva ícone).
 *  Vale igual nos dois modos: o ponto usa o -fg do tint, que é medido em claro e em escuro. */
const CHIP_FILLED = { error: true, warning: true };

function ToneChip({ tone = 'neutral', icon, label, strong = false }) {
  const t = tint(tone);
  if (!CHIP_FILLED[tone]) return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%', flexShrink: 0, minWidth: 0 }}>
      <Box aria-hidden="true" component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.fg, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: tone === 'neutral' ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: strong ? 'var(--fw-bold)' : 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, maxWidth: '100%',
        bgcolor: t.bg, border: '1px solid', borderColor: strong ? t.main : t.border,
        borderRadius: 999, px: 1.125, py: '3px',
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg }}>{icon}</Box>}
      <Typography variant="caption" sx={{ color: t.fg, fontWeight: strong ? 'var(--fw-bold)' : 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Chip neutro de atributo (tipo da página, modelo escolhido). */
function MetaChip({ icon, label }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, maxWidth: '100%',
        bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)',
        borderRadius: 999, px: 1.125, py: '3px',
      }}
    >
      <Box component="span" sx={{ display: 'flex', color: 'var(--text-secondary)' }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Data em texto, já pronta no vm ("no ar desde 12/08", "editada ontem"). */
function MetaDate({ icon, label }) {
  if (!label) return null;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Box component="span" sx={{ display: 'flex', color: 'var(--text-secondary)' }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{label}</Typography>
    </Box>
  );
}

/** Aviso em tint dentro de um bloco/diálogo. */
function NoteRow({ tone = 'info', icon, children, role, sx }) {
  const t = tint(tone);
  return (
    <Box
      role={role}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1.25, ...sx,
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg, mt: '1px', flexShrink: 0 }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, color: t.fg }}>{children}</Box>
    </Box>
  );
}

/** Bloco de estado da tela (erro). Mesmo formato de Serviços/Ausências. */
function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/** Cabeçalho de grupo (só quando a lista tem mais de um grupo). */
function GroupHead({ icon, title, hint }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25, mt: 0.5 }}>
      <Box component="span" sx={{ display: 'flex', color: 'var(--text-secondary)', mt: '1px' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" component="h2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>
          {title}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O VEREDITO — responde "está tudo certo?" antes de a pessoa ler linha por linha
   ═══════════════════════════════════════════════════════════════════════════ */

function VerdictBand({ summary }) {
  const s = summary || {};
  const t = tint(s.headlineTone);
  const Icon = HEADLINE_ICON[s.headlineTone] || LanguageRounded;

  // Rótulos escolhidos para servir a qualquer número (sem concordância a calcular).
  const stats = [
    { key: 'published', value: s.published, label: 'no ar', tone: 'success', Icon: CheckCircleRounded },
    { key: 'pending', value: s.pending, label: 'sem publicar', tone: 'warning', Icon: WarningAmberRounded },
    { key: 'unpublished', value: s.unpublished, label: 'fora do ar', tone: 'error', Icon: CloudOffRounded },
    { key: 'drafts', value: s.drafts, label: 'em rascunho', tone: 'neutral', Icon: EditNoteRounded },
  ].filter((x) => x.value > 0);

  if (!s.headline && stats.length === 0) return null;

  return (
    <Box
      sx={{
        ...CARD_SX, bgcolor: t.bg, borderColor: t.border,
        p: { xs: 2, sm: 2.25 }, mb: 'var(--space-3)',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
        gap: { xs: 1.75, sm: 2.5 },
      }}
    >
      {s.headline && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 320px' }}>
          <Icon sx={{ fontSize: 32, color: t.fg, flexShrink: 0 }} />
          <Typography variant="h6" component="p" sx={{ ...HEAD, color: t.fg, lineHeight: 1.25, textWrap: 'pretty' }}>
            {s.headline}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {stats.map((x) => {
          const st = tint(x.tone);
          return (
            <Box
              key={x.key}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, minHeight: 44,
                px: 1.5, py: 0.75, borderRadius: 'var(--radius-sm)',
                bgcolor: 'var(--surface)', border: '1px solid', borderColor: st.border,
              }}
            >
              <x.Icon sx={{ fontSize: 18, color: st.fg, flexShrink: 0 }} />
              <Typography variant="subtitle1" component="p" sx={{ ...HEAD, color: st.fg, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {x.value}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                {x.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O ENDEREÇO — o que a pessoa realmente faz nesta tela é copiar um link
   ═══════════════════════════════════════════════════════════════════════════ */

/** O domínio próprio: a única coisa desta tela que o cliente pagou. */
function CustomDomainBlock({ page, a }) {
  const d = page.customDomain;
  const t = tint(d.statusTone);
  return (
    <Box
      sx={{
        bgcolor: 'var(--surface)', border: '1px solid', borderColor: t.border,
        borderRadius: 'var(--radius-sm)', p: 1.5,
        display: 'flex', flexDirection: 'column', gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
        <VerifiedRounded sx={{ fontSize: 18, color: t.fg }} aria-hidden="true" />
        <Typography variant="overline" sx={{ color: t.fg, fontWeight: 'var(--fw-bold)', letterSpacing: '.08em', lineHeight: 1.6 }}>
          Domínio próprio
        </Typography>
        <ToneChip
          tone={d.statusTone}
          icon={d.isLive ? <CheckCircleRounded sx={{ fontSize: 14 }} /> : <HourglassTopRounded sx={{ fontSize: 14 }} />}
          label={d.statusLabel}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" component="p" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', minWidth: 0 }}>
          {d.domain}
        </Typography>
        {d.isLive ? (
          <Button
            onClick={() => a.onOpenDomain?.(page.id)}
            startIcon={<OpenInNewRounded sx={{ fontSize: 18 }} />}
            sx={{ ...OUTLINE_BTN_SX, minHeight: 40 }}
          >
            Abrir
          </Button>
        ) : (
          // isLive: false — comprado, apontando, ainda não responde. Dizer "No ar" seria mentira,
          // e deixar o botão ativo levaria a uma página que não abre.
          <Tooltip title="Este endereço ainda não responde">
            <span>
              <Button disabled startIcon={<HourglassTopRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, minHeight: 40 }}>
                Abrir
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {!d.isLive && (
        <Typography variant="caption" sx={{ color: t.sub, lineHeight: 1.45 }}>
          Enquanto este endereço não responder, entregue o endereço padrão abaixo.
        </Typography>
      )}
    </Box>
  );
}

/**
 * O bloco de endereço de uma página. `live` decide o que a URL significa:
 *  · no ar (published/pending) → endereço para entregar, com "Copiar link" rotulado e abrir;
 *  · fora do ar / rascunho     → endereço apagado, sem copiar (copiar link morto é armadilha).
 */
function AddressBlock({ page, a }) {
  const live = page.status === 'published' || page.status === 'pending';
  const dead = page.status === 'unpublished';
  const hasDomain = !!page.customDomain;

  return (
    <Box
      sx={{
        bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)',
        borderRadius: 'var(--radius-sm)', p: 1.25,
        display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0,
      }}
    >
      {hasDomain && <CustomDomainBlock page={page} a={a} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        {hasDomain && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>
            Endereço padrão
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
          <Box component="span" sx={{ display: 'flex', color: 'var(--text-secondary)', mt: '2px', flexShrink: 0 }}>
            {dead ? <LinkOffRounded sx={{ fontSize: 18 }} /> : <LinkRounded sx={{ fontSize: 18 }} />}
          </Box>
          <Typography
            variant="body2"
            sx={{
              ...BREAK, minWidth: 0, flex: 1,
              color: live ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: live ? 'var(--fw-medium)' : 'var(--fw-regular)',
              textDecoration: dead ? 'line-through' : 'none',
            }}
          >
            {page.publicUrl}
          </Typography>
        </Box>

        {live && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
            <Button
              onClick={() => a.onCopyUrl?.(page.id)}
              startIcon={<ContentCopyRounded sx={{ fontSize: 18 }} />}
              sx={{ ...OUTLINE_BTN_SX, minHeight: 40, bgcolor: 'var(--surface)' }}
            >
              Copiar link
            </Button>
            <Tooltip title="Abrir em nova aba">
              <IconButton
                aria-label={`Abrir ${page.title} em nova aba`}
                onClick={() => a.onOpenPublic?.(page.id)}
                sx={ICON_BTN_SX}
              >
                <OpenInNewRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A LINHA DE UMA PÁGINA (published / pending / unpublished)
   ═══════════════════════════════════════════════════════════════════════════ */

function PageCard({ page, a, onOpenMenu }) {
  const t = tint(page.statusTone);
  const meta = STATUS_META[page.status] || STATUS_META.draft;
  const isAlert = meta.weight === 'alert';
  const TypeIcon = TYPE_ICON[page.type] || LanguageRounded;
  const StatusIcon = meta.Icon;

  return (
    <Box
      component="li"
      sx={{
        ...CARD_SX,
        borderColor: isAlert ? t.border : 'var(--divider)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        transition: 'box-shadow .15s, border-color .15s',
        '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: isAlert ? t.main : 'var(--color-primary-light)' },
      }}
    >
      {/* PESO ALTO: a faixa diz o que o VISITANTE está vendo e traz a saída no mesmo lugar. */}
      {isAlert && (
        <Box
          sx={{
            bgcolor: t.bg, borderBottom: '1px solid', borderColor: t.border,
            px: { xs: 2, sm: 2.25 }, py: 1.5,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5,
          }}
        >
          <StatusIcon sx={{ fontSize: 24, color: t.fg, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
            <Typography variant="subtitle2" component="p" sx={{ color: t.fg, fontWeight: 'var(--fw-bold)' }}>
              {page.statusLabel}
            </Typography>
            <Typography variant="body2" sx={{ color: t.fg, lineHeight: 1.4, textWrap: 'pretty' }}>
              {page.statusHint}
            </Typography>
          </Box>
          {page.canPublish && (
            <Button
              onClick={() => a.onPublish?.(page.id)}
              startIcon={<PublishRounded sx={{ fontSize: 20 }} />}
              disableElevation
              sx={PRIMARY_BTN_SX}
            >
              {page.status === 'pending' ? 'Publicar alterações' : 'Publicar de novo'}
            </Button>
          )}
        </Box>
      )}

      <Box
        sx={{
          p: { xs: 2, sm: 2.25 },
          display: 'grid', gap: { xs: 1.75, lg: 2.5 }, alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(320px, 420px)' },
        }}
      >
        {/* identidade */}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" component="h3" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', minWidth: 0 }}>
              {page.title}
            </Typography>
            {/* PESO CALMO: chip pequeno, sem faixa, sem botão preenchido. */}
            {!isAlert && (
              <ToneChip tone={page.statusTone} icon={<StatusIcon sx={{ fontSize: 14 }} />} label={page.statusLabel} />
            )}
          </Box>

          {!isAlert && (
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mt: '2px', lineHeight: 1.45 }}>
              {page.statusHint}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
            <MetaChip icon={<TypeIcon sx={{ fontSize: 14 }} />} label={page.typeLabel} />
            {page.templateName && <MetaChip icon={<PaletteRounded sx={{ fontSize: 14 }} />} label={page.templateName} />}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.75, rowGap: 0.5, mt: 1 }}>
            <MetaDate icon={<EventAvailableRounded sx={{ fontSize: 15 }} />} label={page.publishedAtLabel || page.createdAtLabel} />
            <MetaDate icon={<HistoryRounded sx={{ fontSize: 15 }} />} label={page.updatedAtLabel} />
          </Box>
        </Box>

        {/* endereço */}
        <AddressBlock page={page} a={a} />
      </Box>

      {/* ações da linha: uma visível, o resto no menu — em vez dos cinco ícones em fila de hoje */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.25 }, py: 1.25,
          borderTop: '1px solid', borderColor: 'var(--divider)',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75,
        }}
      >
        {page.status !== 'unpublished' && (
          <Button
            onClick={() => a.onPreview?.(page.id)}
            startIcon={<VisibilityRounded sx={{ fontSize: 18 }} />}
            sx={OUTLINE_BTN_SX}
          >
            {page.status === 'pending' ? 'Ver o que está no ar' : 'Ver a página'}
          </Button>
        )}
        <Button
          onClick={() => a.onEdit?.(page.id)}
          startIcon={<EditRounded sx={{ fontSize: 18 }} />}
          sx={page.status === 'unpublished' ? OUTLINE_BTN_SX : QUIET_BTN_SX}
        >
          Editar
        </Button>
        <Tooltip title="Mais ações">
          <IconButton aria-label={`Mais ações de ${page.title}`} onClick={(e) => onOpenMenu(e.currentTarget, page)} sx={ICON_BTN_SX}>
            <MoreVertRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O LADRILHO DE RASCUNHO — PESO BAIXO: trabalho começado que não virou nada
   ═══════════════════════════════════════════════════════════════════════════ */

function DraftTile({ page, a, onOpenMenu }) {
  const TypeIcon = TYPE_ICON[page.type] || LanguageRounded;
  return (
    <Box
      component="li"
      sx={{
        bgcolor: 'var(--bg-page)', border: '1px dashed', borderColor: 'var(--divider)',
        borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
        p: { xs: 1.75, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 1,
        transition: 'border-color .15s, background-color .15s',
        '&:hover': { borderColor: 'var(--color-primary-light)', bgcolor: 'var(--surface)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <EditNoteRounded sx={{ fontSize: 20, color: 'var(--text-secondary)', flexShrink: 0, mt: '2px' }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" component="h3" sx={{ ...BREAK, fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
            {page.title}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
            {page.statusHint}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        <MetaChip icon={<TypeIcon sx={{ fontSize: 14 }} />} label={page.typeLabel} />
        {page.templateName && <MetaChip icon={<PaletteRounded sx={{ fontSize: 14 }} />} label={page.templateName} />}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}>
        <MetaDate icon={<HistoryRounded sx={{ fontSize: 15 }} />} label={page.updatedAtLabel} />
        <MetaDate icon={<CalendarMonthRounded sx={{ fontSize: 15 }} />} label={page.createdAtLabel} />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 'auto', pt: 0.5 }}>
        <Button onClick={() => a.onEdit?.(page.id)} startIcon={<EditRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, bgcolor: 'var(--surface)' }}>
          Continuar
        </Button>
        {page.canPublish && (
          <Button onClick={() => a.onPublish?.(page.id)} startIcon={<PublishRounded sx={{ fontSize: 18 }} />} sx={QUIET_BTN_SX}>
            Publicar
          </Button>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Mais ações">
            <IconButton aria-label={`Mais ações de ${page.title}`} onClick={(e) => onOpenMenu(e.currentTarget, page)} sx={ICON_BTN_SX}>
              <MoreVertRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A OFERTA DE DOMÍNIO — elemento DESTA tela, depois da lista (continuação)
   ═══════════════════════════════════════════════════════════════════════════ */

function DomainOffer({ offer, a }) {
  return (
    <Box
      sx={{
        ...CARD_SX, bgcolor: CTA_SOFT, borderColor: CTA_EDGE,
        mt: 'var(--space-3)', p: { xs: 2, sm: 3 },
        display: 'grid', gap: { xs: 2, md: 3 }, alignItems: 'center',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(240px, 300px)' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageRounded sx={{ fontSize: 20, color: CTA_FILL_DARK }} aria-hidden="true" />
          <Typography variant="overline" sx={{ color: CTA_FILL_DARK, fontWeight: 'var(--fw-bold)', letterSpacing: '.1em', lineHeight: 1.6 }}>
            Domínio próprio
          </Typography>
        </Box>
        <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1.2, mt: 0.5, textWrap: 'pretty' }}>
          {offer.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', mt: 1, maxWidth: 620, lineHeight: 1.5 }}>
          {offer.pitch}
        </Typography>

        {offer.suggestions?.length > 0 && (
          <Box sx={{ mt: 1.75 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', mb: 0.75 }}>
              Livres agora:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {offer.suggestions.map((s) => (
                <Box
                  key={s}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.625, maxWidth: '100%',
                    bgcolor: 'var(--surface)', border: '1px solid', borderColor: CTA_EDGE,
                    borderRadius: 999, px: 1.25, py: '5px',
                  }}
                >
                  <CheckRounded sx={{ fontSize: 15, color: CTA_FILL }} aria-hidden="true" />
                  <Typography variant="body2" sx={{ ...BREAK, color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
                    {s}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          bgcolor: 'var(--surface)', border: '1px solid', borderColor: CTA_EDGE,
          borderRadius: 'var(--radius-card)', p: { xs: 1.75, sm: 2 },
          display: 'flex', flexDirection: 'column', gap: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.75 }}>
          <Typography variant="h4" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1 }}>
            {offer.priceLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>
            {offer.periodLabel}
          </Typography>
        </Box>
        <Button
          onClick={() => a.onOpenDomainOffer?.()}
          endIcon={<ArrowForwardRounded sx={{ fontSize: 20 }} />}
          disableElevation
          sx={{ ...CTA_BTN_SX, width: '100%' }}
        >
          {offer.ctaLabel}
        </Button>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>
          Vale para a sua página{' '}
          <Box component="strong" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>{offer.pageTitle}</Box>.
        </Typography>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O VAZIO — não é "nenhum resultado": é uma pessoa sem presença na internet
   ═══════════════════════════════════════════════════════════════════════════ */

function EmptyPanel({ empty, a }) {
  return (
    <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
      <Box
        sx={{
          bgcolor: CTA_SOFT, borderBottom: '1px solid', borderColor: CTA_EDGE,
          px: { xs: 2.5, sm: 5 }, py: { xs: 4, sm: 6 },
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 72, height: 72, borderRadius: '50%', mb: 2.5,
            bgcolor: 'var(--surface)', border: '1px solid', borderColor: CTA_EDGE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <LanguageRounded sx={{ fontSize: 36, color: CTA_FILL }} />
        </Box>
        <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', maxWidth: 620, lineHeight: 1.2, textWrap: 'pretty' }}>
          {empty.title}
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--text-primary)', maxWidth: 620, mt: 1.5, lineHeight: 1.55 }}>
          {empty.message}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button onClick={() => a.onCreate?.()} startIcon={<AddRounded />} disableElevation sx={CTA_BTN_SX}>
            {empty.ctaLabel}
          </Button>
        </Box>
      </Box>

      {empty.reasons?.length > 0 && (
        <Box
          component="ul"
          sx={{
            listStyle: 'none', m: 0, p: { xs: 2, sm: 3 },
            display: 'grid', gap: { xs: 1.5, sm: 2 },
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          }}
        >
          {empty.reasons.map((r, i) => {
            const Icon = REASON_ICON[i] || CheckCircleRounded;
            return (
              <Box key={r} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    bgcolor: 'var(--color-primary-soft)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5, textWrap: 'pretty' }}>
                  {r}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Esqueleto — com o formato do que vai chegar (veredito + linhas), não caixas genéricas
   ═══════════════════════════════════════════════════════════════════════════ */

const SK = { bgcolor: 'var(--divider)' };

function LoadingSkeleton() {
  return (
    <Box aria-busy="true" aria-live="polite">
      <Skeleton variant="rounded" height={84} sx={{ ...SK, borderRadius: 'var(--radius-card)', mb: 'var(--space-3)' }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ ...CARD_SX, p: { xs: 2, sm: 2.25 } }}>
            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(320px, 420px)' } }}>
              <Box>
                <Skeleton variant="text" width="55%" height={26} sx={SK} />
                <Skeleton variant="text" width="80%" height={16} sx={SK} />
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
                  <Skeleton variant="rounded" width={104} height={22} sx={{ ...SK, borderRadius: 999 }} />
                  <Skeleton variant="rounded" width={124} height={22} sx={{ ...SK, borderRadius: 999 }} />
                </Box>
              </Box>
              <Skeleton variant="rounded" height={92} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DIÁLOGOS — textos sempre do vm; erro DENTRO do diálogo (não em snackbar)
   ═══════════════════════════════════════════════════════════════════════════ */

function DialogError({ error }) {
  if (!error) return null;
  return (
    <NoteRow tone="error" role="alert" icon={<ErrorRounded sx={{ fontSize: 20 }} />} sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{error}</Typography>
    </NoteRow>
  );
}

/** Qual página o diálogo está tratando — sem isto a confirmação fica abstrata. */
function DialogSubject({ title }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)',
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1.25, mb: 2,
      }}
    >
      <LanguageRounded sx={{ fontSize: 18, color: 'var(--text-secondary)', flexShrink: 0 }} aria-hidden="true" />
      <Typography variant="subtitle2" component="p" sx={{ ...BREAK, color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>
        {title}
      </Typography>
    </Box>
  );
}

/** O seletor de tipo: a porta de entrada. A escolha define o site da pessoa por meses. */
function TypeDialog({ d, a }) {
  return (
    <Dialog open={!!d.open} onClose={() => a.onCloseTypeDialog?.()} maxWidth="md" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
      <DialogTitle sx={DIALOG_TITLE_SX}>{d.title}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2.5, lineHeight: 1.5 }}>
          {d.subtitle}
        </Typography>
        <Box sx={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>
          {(d.options || []).map((o) => {
            const Icon = TYPE_ICON[o.type] || LanguageRounded;
            return (
              <ButtonBase
                key={o.type}
                focusRipple
                disabled={!o.enabled}
                onClick={() => a.onSelectType?.(o.type)}
                aria-label={`Criar página do tipo ${o.label}`}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left',
                  height: '100%', p: 2, gap: 1,
                  borderRadius: 'var(--radius-card)',
                  border: o.recommended ? '2px solid' : '1px solid',
                  borderColor: o.recommended ? 'var(--color-primary)' : 'var(--divider)',
                  bgcolor: o.recommended ? 'var(--color-primary-soft)' : 'var(--surface)',
                  transition: 'box-shadow .15s, border-color .15s, transform .15s',
                  '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: 'var(--color-primary)' },
                  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
                  '&.Mui-disabled': { opacity: 0.55 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      bgcolor: o.recommended ? 'var(--surface)' : 'var(--color-primary-soft)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />
                  </Box>
                  {o.recommended && <ToneChip tone="brand" strong icon={<StarRounded sx={{ fontSize: 14 }} />} label="Recomendado" />}
                  {o.badge && <ToneChip tone="info" label={o.badge} />}
                  {!o.enabled && <ToneChip tone="neutral" label="Indisponível" />}
                </Box>

                <Typography variant="subtitle1" component="p" sx={{ ...HEAD, color: 'var(--text-primary)' }}>
                  {o.label}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                  {o.description}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                  {(o.highlights || []).map((h) => (
                    <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <CheckRounded sx={{ fontSize: 16, color: 'var(--color-primary)', flexShrink: 0, mt: '2px' }} />
                      <Typography variant="caption" sx={{ color: 'var(--text-primary)', lineHeight: 1.45 }}>
                        {h}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {o.enabled && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto', pt: 1.25 }}>
                    <Typography variant="body2" sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>
                      Criar esta
                    </Typography>
                    <ArrowForwardRounded sx={{ fontSize: 18, color: 'var(--color-primary-dark)' }} />
                  </Box>
                )}
              </ButtonBase>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button onClick={() => a.onCloseTypeDialog?.()} sx={QUIET_BTN_SX}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}

function PublishDialog({ d, a }) {
  return (
    <Dialog
      open={!!d.open}
      onClose={() => { if (!d.saving) a.onCancelPublish?.(); }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: DIALOG_PAPER_SX }}
    >
      <DialogTitle sx={DIALOG_TITLE_SX}>{d.title}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <DialogSubject title={d.pageTitle} />
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {d.message}
        </Typography>
        <DialogError error={d.error} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button onClick={() => a.onCancelPublish?.()} disabled={d.saving} sx={QUIET_BTN_SX}>Cancelar</Button>
        <Button
          onClick={() => a.onConfirmPublish?.()}
          disabled={d.saving}
          disableElevation
          startIcon={d.saving
            ? <CircularProgress size={18} sx={{ color: 'var(--on-primary)' }} />
            : <PublishRounded sx={{ fontSize: 20 }} />}
          sx={PRIMARY_BTN_SX}
        >
          {d.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UnpublishDialog({ d, a }) {
  return (
    <Dialog
      open={!!d.open}
      onClose={() => { if (!d.saving) a.onCancelUnpublish?.(); }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: DIALOG_PAPER_SX }}
    >
      <DialogTitle sx={DIALOG_TITLE_SX}>{d.title}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <DialogSubject title={d.pageTitle} />
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {d.message}
        </Typography>
        {d.warning && (
          <NoteRow tone="warning" icon={<WarningAmberRounded sx={{ fontSize: 20 }} />} sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.45, fontWeight: 'var(--fw-medium)' }}>{d.warning}</Typography>
          </NoteRow>
        )}
        <DialogError error={d.error} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button onClick={() => a.onCancelUnpublish?.()} disabled={d.saving} sx={QUIET_BTN_SX}>Cancelar</Button>
        <Button
          onClick={() => a.onConfirmUnpublish?.()}
          disabled={d.saving}
          disableElevation
          startIcon={d.saving
            ? <CircularProgress size={18} sx={{ color: 'var(--on-primary)' }} />
            : <CloudOffRounded sx={{ fontSize: 20 }} />}
          sx={DANGER_BTN_SX}
        >
          {d.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Exclusão. Quando `blocked`, negar não basta: o diálogo mostra o motivo e oferece a saída
 * (`unblockLabel` → onUnblockDelete), com o "Excluir" visivelmente travado.
 */
function DeleteDialog({ d, a }) {
  return (
    <Dialog
      open={!!d.open}
      onClose={() => { if (!d.saving) a.onCancelDelete?.(); }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: DIALOG_PAPER_SX }}
    >
      <DialogTitle sx={DIALOG_TITLE_SX}>{d.title}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <DialogSubject title={d.pageTitle} />
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {d.message}
        </Typography>
        {d.blocked && d.blockedReason && (
          <NoteRow tone="warning" icon={<CloudOffRounded sx={{ fontSize: 20 }} />} sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.45, fontWeight: 'var(--fw-medium)' }}>{d.blockedReason}</Typography>
          </NoteRow>
        )}
        <DialogError error={d.error} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={() => a.onCancelDelete?.()} disabled={d.saving} sx={QUIET_BTN_SX}>Cancelar</Button>
        <Button
          onClick={() => a.onConfirmDelete?.()}
          disabled={d.blocked || d.saving}
          disableElevation
          startIcon={d.saving
            ? <CircularProgress size={18} sx={{ color: 'var(--on-primary)' }} />
            : <DeleteOutlineRounded sx={{ fontSize: 20 }} />}
          sx={DANGER_BTN_SX}
        >
          {d.confirmLabel}
        </Button>
        {d.blocked && d.unblockLabel && (
          <Button
            onClick={() => a.onUnblockDelete?.()}
            disabled={d.saving}
            disableElevation
            startIcon={<CloudOffRounded sx={{ fontSize: 20 }} />}
            sx={PRIMARY_BTN_SX}
          >
            {d.unblockLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function PreviewDialog({ d, a }) {
  return (
    <Dialog
      open={!!d.open}
      onClose={() => a.onClosePreview?.()}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { ...DIALOG_PAPER_SX, height: { xs: '100%', sm: '86vh' } } }}
    >
      <DialogTitle sx={{ ...DIALOG_TITLE_SX, pb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box component="span" sx={{ ...BREAK, flex: 1, minWidth: 0 }}>{d.pageTitle}</Box>
        <IconButton aria-label="Fechar a pré-visualização" onClick={() => a.onClosePreview?.()} sx={ICON_BTN_SX}>
          <CloseRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* barra de endereço: é a página pública, no endereço público */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'var(--bg-page)', borderTop: '1px solid', borderBottom: '1px solid',
            borderColor: 'var(--divider)', px: 2, py: 1,
          }}
        >
          <LinkRounded sx={{ fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="caption" sx={{ ...BREAK, color: 'var(--text-secondary)', minWidth: 0 }}>
            {d.url}
          </Typography>
        </Box>
        <Box
          component="iframe"
          src={d.url}
          title={`Pré-visualização de ${d.pageTitle}`}
          sx={{ border: 0, width: '100%', flex: 1, minHeight: { xs: 320, sm: 420 }, bgcolor: 'var(--surface)' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => a.onClosePreview?.()} sx={QUIET_BTN_SX}>{d.closeLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A VIEW
   ═══════════════════════════════════════════════════════════════════════════ */

export default function PagesView({ vm, actions }) {
  const a = actions || {};
  // UI-only: qual linha abriu o menu de "mais ações". Nenhum dado mora aqui.
  const [menu, setMenu] = useState({ el: null, page: null });
  const openMenu = (el, page) => setMenu({ el, page });
  const closeMenu = () => setMenu({ el: null, page: null });
  const runFromMenu = (fn) => { closeMenu(); fn?.(); };

  const pages = vm.pages || [];
  const state = vm.state;

  // AGRUPAMENTO SÓ VISUAL: a ordem dentro de cada grupo continua sendo a do servidor
  // (vm.pages já vem com o que pede ação na frente). Nada é recalculado.
  const alertPages = pages.filter((p) => p.status === 'pending' || p.status === 'unpublished');
  const livePages = pages.filter((p) => p.status === 'published');
  const drafts = pages.filter((p) => p.status === 'draft');
  const groupCount = [alertPages, livePages, drafts].filter((g) => g.length > 0).length;
  const heads = groupCount > 1;

  const createButton = (
    <Button onClick={() => a.onCreate?.()} startIcon={<AddRounded />} disableElevation sx={PRIMARY_BTN_SX}>
      {vm.createLabel}
    </Button>
  );

  let body = null;

  if (state === 'loading') {
    body = <LoadingSkeleton />;
  } else if (state === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title="Não foi possível carregar suas páginas"
        description={vm.errorMessage}
        action={(
          <Button onClick={() => a.onRetry?.()} startIcon={<ReplayRounded />} sx={{ ...OUTLINE_BTN_SX, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>
            Tentar novamente
          </Button>
        )}
      />
    );
  } else if (state === 'empty') {
    body = <EmptyPanel empty={vm.empty || {}} a={a} />;
  } else {
    body = (
      <>
        <VerdictBand summary={vm.summary} />

        {alertPages.length > 0 && (
          <Box component="section" sx={{ mb: livePages.length || drafts.length ? 'var(--space-3)' : 0 }}>
            {heads && (
              <GroupHead
                icon={<WarningAmberRounded sx={{ fontSize: 18 }} />}
                title="Pedem sua atenção"
                hint="O que o visitante vê agora não é o que você quer."
              />
            )}
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {alertPages.map((p) => <PageCard key={p.id} page={p} a={a} onOpenMenu={openMenu} />)}
            </Box>
          </Box>
        )}

        {livePages.length > 0 && (
          <Box component="section" sx={{ mb: drafts.length ? 'var(--space-3)' : 0 }}>
            {heads && (
              <GroupHead
                icon={<CheckCircleRounded sx={{ fontSize: 18 }} />}
                title="No ar e em dia"
                hint="Estes endereços podem ser entregues como estão."
              />
            )}
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {livePages.map((p) => <PageCard key={p.id} page={p} a={a} onOpenMenu={openMenu} />)}
            </Box>
          </Box>
        )}

        {drafts.length > 0 && (
          <Box component="section">
            {heads && (
              <GroupHead
                icon={<EditNoteRounded sx={{ fontSize: 18 }} />}
                title="Rascunhos"
                hint="Nunca foram publicados — ninguém consegue ver."
              />
            )}
            <Box
              component="ul"
              sx={{
                listStyle: 'none', m: 0, p: 0,
                display: 'grid', gap: 'var(--space-2)',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(300px, 1fr))' },
              }}
            >
              {drafts.map((p) => <DraftTile key={p.id} page={p} a={a} onOpenMenu={openMenu} />)}
            </Box>
          </Box>
        )}

        {vm.domainOffer?.visible && <DomainOffer offer={vm.domainOffer} a={a} />}
      </>
    );
  }

  const mp = menu.page;

  return (
    <Box sx={{ color: 'var(--text-primary)' }}>
      {/* Moldura da tela: título e botão não dependem de dado, então não piscam entre estados. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <LanguageRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} />
            <Typography variant="h1" component="h1" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>
              {vm.title}
            </Typography>
            {/* recarga COM lista na tela: indicador discreto, a lista não pisca */}
            <Box aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 20, opacity: vm.refreshing ? 1 : 0, transition: 'opacity .3s ease' }}>
              <CircularProgress size={13} sx={{ color: 'var(--text-secondary)' }} />
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                {vm.refreshing ? 'Atualizando…' : ''}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
            {vm.subtitle}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {(state === 'ready' || state === 'empty') && (
            <Tooltip title="Atualizar">
              <IconButton
                aria-label="Atualizar a lista de páginas"
                onClick={() => a.onRefresh?.()}
                disabled={!!vm.refreshing}
                sx={{ ...ICON_BTN_SX, border: '1px solid', borderColor: 'var(--divider)', borderRadius: 'var(--radius-sm)' }}
              >
                <RefreshRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          {state !== 'empty' && createButton}
        </Box>
      </Box>

      {body}

      {/* menu de "mais ações" de uma linha */}
      <Menu
        open={!!menu.el}
        anchorEl={menu.el}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: MENU_PAPER_SX } }}
        MenuListProps={{ 'aria-label': mp ? `Ações de ${mp.title}` : 'Ações da página' }}
      >
        {mp && mp.status === 'draft' && (
          <MenuItem onClick={() => runFromMenu(() => a.onPreview?.(mp.id))} sx={{ minHeight: 44 }}>
            <ListItemIcon sx={{ minWidth: 34, color: 'var(--text-secondary)' }}><VisibilityRounded sx={{ fontSize: 20 }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>Pré-visualizar</Typography>
          </MenuItem>
        )}
        {mp && mp.canUnpublish && (
          <MenuItem onClick={() => runFromMenu(() => a.onUnpublish?.(mp.id))} sx={{ minHeight: 44 }}>
            <ListItemIcon sx={{ minWidth: 34, color: 'var(--text-secondary)' }}><CloudOffRounded sx={{ fontSize: 20 }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>Tirar do ar</Typography>
          </MenuItem>
        )}
        {mp && (
          // Excluir NUNCA fica mudo nem desabilitado: com canDelete=false o clique abre o
          // diálogo que explica o bloqueio e oferece a saída ("Tirar do ar agora"). O motivo
          // (blockedReason) já aparece aqui, para o clique não ser surpresa.
          <MenuItem onClick={() => runFromMenu(() => a.onDelete?.(mp.id))} sx={{ minHeight: 44, alignItems: 'flex-start', py: 1 }}>
            <ListItemIcon sx={{ minWidth: 34, color: 'var(--color-error)', mt: '2px' }}><DeleteOutlineRounded sx={{ fontSize: 20 }} /></ListItemIcon>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: 'var(--color-error)', fontWeight: 'var(--fw-medium)' }}>Excluir</Typography>
              {!mp.canDelete && mp.blockedReason && (
                <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', whiteSpace: 'normal', maxWidth: 200, lineHeight: 1.4 }}>
                  {mp.blockedReason}
                </Typography>
              )}
            </Box>
          </MenuItem>
        )}
      </Menu>

      <TypeDialog d={vm.typeDialog || {}} a={a} />
      <PublishDialog d={vm.publishDialog || {}} a={a} />
      <UnpublishDialog d={vm.unpublishDialog || {}} a={a} />
      <DeleteDialog d={vm.deleteDialog || {}} a={a} />
      <PreviewDialog d={vm.previewDialog || {}} a={a} />

      {/* feedback efêmero (copiou o link) NÃO mora aqui.
          O projeto padroniza toda confirmação no `AppSnackbar` (wrapper com Alert, posição e
          tempo iguais em ~19 telas), e o eslint proíbe `Snackbar` cru justamente para as telas
          não divergirem. Quem renderiza é o container, lendo `vm.feedback`.
          Foi engano meu no contrato @v2 sugerir que a View desenhasse isso. */}
    </Box>
  );
}
